import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import { PresentationSlide, slideSize } from '../types';
import { PRES } from '../chrome';
import StaticSlideView from './StaticSlideView';
import {
  formatPresenterClock,
  formatPresenterElapsed,
  nextPresenterIndex,
  prevPresenterIndex,
  resolvePresenterKey,
} from './presenterControls';
import {
  getHeartbeatIntervalMs,
  heartbeat,
  recordUserActivity,
} from '../../../services/authService';

interface PresenterViewProps {
  slides: PresentationSlide[];
  startIndex: number;
  pageFormat?: string | null;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
}

const PresenterView: React.FC<PresenterViewProps> = ({ slides, startIndex, pageFormat, onClose, onIndexChange }) => {
  const size = slideSize(pageFormat);
  const [index, setIndex] = useState(startIndex);
  const [blackout, setBlackout] = useState(false);
  const [paused, setPaused] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [clock, setClock] = useState(() => formatPresenterClock(new Date()));
  const accumulatedRef = useRef(0);
  const runningSinceRef = useRef(Date.now());

  const setPresenterIndex = useCallback((nextIndex: number) => {
    const clamped = Math.max(0, Math.min(slides.length - 1, nextIndex));
    setIndex(clamped);
    setBlackout(false);
    onIndexChange?.(clamped);
  }, [onIndexChange, slides.length]);

  useEffect(() => {
    recordUserActivity(Date.now(), true);
    void heartbeat();
    const interval = window.setInterval(() => {
      recordUserActivity(Date.now());
      void heartbeat();
    }, getHeartbeatIntervalMs());
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const tick = window.setInterval(() => {
      setClock(formatPresenterClock(new Date()));
      if (paused) return;
      setElapsedMs(accumulatedRef.current + (Date.now() - runningSinceRef.current));
    }, 250);
    return () => window.clearInterval(tick);
  }, [paused]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const action = resolvePresenterKey(event.key);
      if (!action) return;
      event.preventDefault();
      if (action === 'close') {
        onClose();
        return;
      }
      if (action === 'blackout') {
        setBlackout((value) => !value);
        return;
      }
      if (action === 'toggleTimer') {
        setPaused((value) => {
          if (value) {
            runningSinceRef.current = Date.now();
          } else {
            accumulatedRef.current += Date.now() - runningSinceRef.current;
          }
          return !value;
        });
        return;
      }
      if (action === 'next') setPresenterIndex(nextPresenterIndex(index, slides.length));
      if (action === 'prev') setPresenterIndex(prevPresenterIndex(index, slides.length));
      if (action === 'first') setPresenterIndex(0);
      if (action === 'last') setPresenterIndex(slides.length - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, onClose, setPresenterIndex, slides.length]);

  const slide = slides[index];
  const nextSlide = slides[index + 1] || null;
  if (!slide) return null;

  const resetTimer = () => {
    accumulatedRef.current = 0;
    runningSinceRef.current = Date.now();
    setElapsedMs(0);
    setPaused(false);
  };

  const toggleTimer = () => {
    setPaused((value) => {
      if (value) {
        runningSinceRef.current = Date.now();
      } else {
        accumulatedRef.current += Date.now() - runningSinceRef.current;
      }
      return !value;
    });
  };

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1600, bgcolor: '#0b1020', color: '#fff', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.25, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography sx={{ fontWeight: 600, letterSpacing: 0.4 }}>Presentationsläge</Typography>
        <Typography sx={{ opacity: 0.7 }}>{index + 1} / {slides.length}</Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontVariantNumeric: 'tabular-nums', opacity: 0.8 }}>{clock}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.25, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.06)' }}>
          <Typography sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 56 }}>{formatPresenterElapsed(elapsedMs)}</Typography>
          <Tooltip title={paused ? 'Fortsätt timer (P)' : 'Pausa timer (P)'}>
            <IconButton size="small" onClick={toggleTimer} sx={{ color: '#fff' }}>
              {paused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Nollställ timer">
            <IconButton size="small" onClick={resetTimer} sx={{ color: '#fff' }}>
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Tooltip title="Svart skärm (B)">
          <IconButton
            size="small"
            onClick={() => setBlackout((value) => !value)}
            sx={{ color: blackout ? PRES.gold : '#fff' }}
          >
            <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: '#000', border: '1px solid rgba(255,255,255,0.45)' }} />
          </IconButton>
        </Tooltip>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, gap: 2.5, p: 2.5 }}>
        <Box sx={{ flex: 1.6, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
          <Box sx={{ width: `min(100%, calc(72vh * ${size.width} / ${size.height}))`, maxHeight: '72vh' }}>
            <StaticSlideView
              framed
              pageFormat={pageFormat}
              slide={slide}
              blackout={blackout}
              onClick={() => {
                if (blackout) {
                  setBlackout(false);
                  return;
                }
                setPresenterIndex(nextPresenterIndex(index, slides.length));
              }}
            />
          </Box>
        </Box>
        <Box sx={{ width: 360, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 280 }}>
          <Box>
            <Typography variant="caption" sx={{ color: PRES.gold, fontWeight: 700, letterSpacing: 0.8 }}>NEXT</Typography>
            {nextSlide ? (
              <Box sx={{ mt: 1 }}>
                <StaticSlideView framed pageFormat={pageFormat} slide={nextSlide} />
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.65 }}>
                  Slide {index + 2}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 1, height: 202, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" sx={{ opacity: 0.55 }}>Sista sidan</Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, p: 1.75, overflow: 'auto' }}>
            <Typography variant="caption" sx={{ color: PRES.gold, fontWeight: 700, letterSpacing: 0.8 }}>ANTECKNINGAR</Typography>
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap', opacity: 0.9, lineHeight: 1.5 }}>
              {slide.notes || 'Inga anteckningar.'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 1.25 }}>
        <IconButton
          onClick={() => setPresenterIndex(prevPresenterIndex(index, slides.length))}
          disabled={index === 0}
          sx={{ color: '#fff' }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{ minWidth: 72, textAlign: 'center', opacity: 0.75 }}>{index + 1} / {slides.length}</Typography>
        <IconButton
          onClick={() => setPresenterIndex(nextPresenterIndex(index, slides.length))}
          disabled={index >= slides.length - 1}
          sx={{ color: '#fff' }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default PresenterView;
